import { chain } from 'obliterator';
import { sortBy } from 'ramda';
import { Matrix4, Vector3, Mesh } from 'three';
import type { BoxHelper, Box3 } from 'three';

import ViewPort from '../ViewPort';
import { FORCE_VIEW_TYPES, TWEEN_TIMES } from '../constants';
import { isAntiParallel, getMidpoint, isParallel } from '../geom-utils';
import { OBBSimplifiedRepresentation } from '../loaders/OBBSimplifiedRepresentation';
import type { AnyBody } from '../loaders/body';
import { StepEdgeModel } from '../loaders/step/StepEdgeModel';
import { StepFaceModel } from '../loaders/step/StepFaceModel';
import { StepTextModel } from '../loaders/step/StepTextModel';
import { prepareBoundBoxForObjects } from './boundbox-utils';
import {
  ensureCameraFarSettingCompatibleWithMaxDistance,
  extractDefaultViewProps,
  getSafePositionToPreventClippingForOrthographicCamera,
} from './control-utils';
import {
  setVisibilityForFocus,
  setVisibilityForObjectIds,
} from './focus-utils';
import { getBodyPose } from './load-utils';
import type { Renderer, RendererState } from './renderer';
import {
  type TweenStart,
  makeCameraPositionTween,
  makeCameraPositionTweenWithDeflection,
  makeControlTargetTween,
  makeOrthoCursorTween,
  makeUpTween,
  makeViewportTween,
} from './tween-utils';

const resetView = async (
  renderer: Renderer,
  nextState: RendererState,
): Promise<void> => {
  const { bodyOrAssemblyInstanceById, assemblyInstances, focusedBodies } =
    nextState;

  if (!renderer.axisAlignedBoundBox) {
    return;
  }
  const zoom1Viewport = ViewPort.getForBoundingBox(
    (renderer.axisAlignedBoundBox as BoxHelper).geometry.boundingBox as Box3,
    renderer.nodeHeight,
    renderer.nodeWidth,
  );
  const referenceDiagonal = zoom1Viewport.diagonal;

  const {
    cameraPosition: finalPosition,
    controlTarget: finalTarget,
    orthoView: finalViewport,
    up: finalUp,
    maxSceneDistance,
  } = extractDefaultViewProps(
    renderer.focusedBoundBox as BoxHelper,
    renderer.axisAlignedBoundBox as BoxHelper,
    renderer,
  );
  ensureCameraFarSettingCompatibleWithMaxDistance(
    maxSceneDistance,
    renderer.camera,
  );

  const checkpointViewport = finalViewport.clone();
  checkpointViewport.applyScale(1.1);

  const positionTween = makeCameraPositionTweenWithDeflection(
    renderer,
    finalPosition,
    finalUp,
    TWEEN_TIMES.RESET_VIEW,
  );
  const upTween = makeUpTween(renderer, finalUp, TWEEN_TIMES.RESET_VIEW);

  // only turn on whats visible
  setVisibilityForObjectIds(
    nextState,
    chain(nextState.visibleBodies, nextState.visibleStockResultKeys),
  );

  const finalCallBack = () => {
    setVisibilityForObjectIds(
      nextState,
      chain(nextState.visibleBodies, nextState.visibleStockResultKeys),
    );
  };

  const orthoTween = makeOrthoCursorTween(
    renderer,
    renderer.state.orthoView,
    finalViewport,
    checkpointViewport,
    () => setVisibilityForFocus(nextState),
    finalCallBack,
    TWEEN_TIMES.RESET_VIEW / 2,
    TWEEN_TIMES.RESET_VIEW / 2,
  );
  const targetTween = makeControlTargetTween(
    renderer,
    finalTarget,
    TWEEN_TIMES.RESET_VIEW,
  );

  const finalDiagonal = finalViewport.diagonal;
  await renderer.state.onCameraZoomChange(referenceDiagonal / finalDiagonal);

  // hide all materials, except for any that are focused (or child of focused)
  // NOTE: we can assume that the focus id for material is the root assembly id
  let changed = false;
  for (const { isMaterial, isRoot, id, group } of assemblyInstances) {
    if (isMaterial && isRoot && !focusedBodies.includes(id as string)) {
      if (group.visible) {
        group.visible = false;
        changed = true;
      }
    }
  }

  // now update body pose for visibility changes
  if (changed) {
    await renderer.state.onBodyPoseChange(
      getBodyPose(bodyOrAssemblyInstanceById.values()),
    );
  }

  await Promise.all(
    [orthoTween, targetTween, positionTween, upTween].map((tween) =>
      tween.start(),
    ),
  );
};

const zoomToBoundBox = async (
  renderer: Renderer,
  boundBox: BoxHelper,
): Promise<void> => {
  if (!boundBox) {
    return;
  }
  const viewGeoBox = boundBox.geometry.boundingBox as Box3;
  const finalTarget = getMidpoint(viewGeoBox.max, viewGeoBox.min);

  const zoom1Viewport = ViewPort.getForBoundingBox(
    (renderer.axisAlignedBoundBox as BoxHelper).geometry.boundingBox as Box3,
    renderer.nodeHeight,
    renderer.nodeWidth,
  );
  const referenceDiagonal = zoom1Viewport.diagonal;

  const finalViewport = ViewPort.getForBoundingBox(
    boundBox.geometry.boundingBox as Box3,
    renderer.nodeHeight,
    renderer.nodeWidth,
  );

  const viewportTween = makeViewportTween(
    renderer,
    renderer.state.orthoView,
    finalViewport,
    TWEEN_TIMES.ZOOM_TO_FIT,
  );

  // move control target to center of body bounding box while moving camera position
  // to maintain current camera angle
  const separationVector = new Vector3().subVectors(
    finalTarget,
    renderer.trackball.target,
  );
  const { cameraPosition: finalPosition, maxDistance } =
    getSafePositionToPreventClippingForOrthographicCamera({
      desiredCameraPosition: new Vector3().addVectors(
        renderer.camera.position,
        separationVector,
      ),
      controlTarget: finalTarget,
      scene: renderer.scene,
    });
  ensureCameraFarSettingCompatibleWithMaxDistance(maxDistance, renderer.camera);

  const targetTween = makeControlTargetTween(
    renderer,
    finalTarget,
    TWEEN_TIMES.ZOOM_TO_FIT,
  );
  const positionTween = makeCameraPositionTween(
    renderer,
    finalPosition,
    TWEEN_TIMES.ZOOM_TO_FIT,
  );

  // NOTE: we do not have to worry about performing an up tween to keep up and
  // look vector orthogonal because we are keeping the look vector direction the same

  const finalDiagonal = finalViewport.diagonal;
  await renderer.state.onCameraZoomChange(referenceDiagonal / finalDiagonal);

  await Promise.all(
    [viewportTween, targetTween, positionTween].map((tween) => tween.start()),
  );
};

const zoomToFit = async (
  renderer: Renderer,
  nextState: RendererState,
): Promise<void> => {
  const { bodyOrAssemblyInstanceById, forceView } = nextState;
  const payload = forceView?.payload;
  const viewerId = payload?.viewerId;
  const bodyOrAssemblyInstance = bodyOrAssemblyInstanceById.get(
    viewerId as string,
  ) as AnyBody;

  if (bodyOrAssemblyInstance?.isLoaded) {
    await zoomToBoundBox(
      renderer,
      bodyOrAssemblyInstance.boundBox as BoxHelper,
    );
  }
};

const zoomToSelection = (renderer: Renderer): Promise<void> => {
  const {
    caliperObjects,
    historicalCaliperObjects,
    historicalSelectedObjects,
    selectedObjects,
  } = renderer.state;
  const selectionBoundBox = prepareBoundBoxForObjects(
    (function* () {
      for (const { obj } of chain(
        caliperObjects,
        historicalCaliperObjects,
        historicalSelectedObjects,
        selectedObjects,
      )) {
        if (obj) {
          if (obj instanceof OBBSimplifiedRepresentation) {
            continue;
          }
          yield obj instanceof StepEdgeModel ||
          obj instanceof StepFaceModel ||
          obj instanceof StepTextModel ||
          obj instanceof Mesh
            ? obj
            : obj.renderable;
        }
      }
    })(),
  );

  return zoomToBoundBox(renderer, selectionBoundBox);
};

const sortByAngle = sortBy((x: [Vector3, Vector3, number]) => x[2]);
const getNearestProjectedAxis = (
  planeNormal: Vector3,
  vector: Vector3,
): Vector3 => {
  const principleAxes = [
    new Vector3(1, 0, 0),
    new Vector3(-1, 0, 0),
    new Vector3(0, 1, 0),
    new Vector3(0, -1, 0),
    new Vector3(0, 0, 1),
    new Vector3(0, 0, -1),
  ];
  const angles: [Vector3, Vector3, number][] = [];
  for (const axis of principleAxes) {
    const projected: Vector3 = axis.clone().projectOnPlane(planeNormal);
    projected.normalize();
    angles.push([axis, projected, projected.angleTo(vector)]);
  }
  const sortedAngles = sortByAngle(angles);
  for (const x of sortedAngles) {
    const isAligned =
      isParallel(x[0], planeNormal) || isAntiParallel(x[0], planeNormal);
    if (!isAligned) {
      return x[0];
    }
  }
  return sortedAngles[0][0];
};

const viewNormalTo = async (
  renderer: Renderer,
  nextState: RendererState,
): Promise<void> => {
  const { forceView } = nextState;
  const payload = forceView?.payload;
  const intersection = payload?.intersection;
  const { face, object, body: bodyId } = intersection ?? {};
  const normal = face?.normal;
  if (
    !normal ||
    !object ||
    !bodyId ||
    object instanceof OBBSimplifiedRepresentation
  ) {
    return;
  }
  normal.transformDirection(object.matrixWorld);

  const { bodyOrAssemblyInstanceById } = renderer.state;
  const body = bodyOrAssemblyInstanceById.get(bodyId as string) as AnyBody;
  if (!body) {
    return;
  }

  const tweens: TweenStart[] = [];

  const zoomToFitViewport = ViewPort.getForBoundingBox(
    body.boundBox.geometry.boundingBox as Box3,
    renderer.nodeHeight,
    renderer.nodeWidth,
  );
  const currentViewport = renderer.state.orthoView;

  let finalTarget = renderer.state.controlTarget.clone();

  // if the current viewport is greater than the zoom to fit viewport of
  // the body the intersection object belongs to, enact a zoom to fit to that
  // object boundbox (including camera position and target update)
  // if the current viewport is more zoomed than the zoomToFit viewport of the body
  // then dont adjust the viewport at all and only enact the normal view adjustment
  if (currentViewport.diagonal > zoomToFitViewport.diagonal) {
    const viewGeoBox = (body.boundBox as BoxHelper).geometry
      .boundingBox as Box3;
    finalTarget = getMidpoint(viewGeoBox.max, viewGeoBox.min);

    const targetTween = makeControlTargetTween(
      renderer,
      finalTarget,
      TWEEN_TIMES.NORMAL_TO,
    );
    if (targetTween) tweens.push(targetTween);

    const zoom1Viewport = ViewPort.getForBoundingBox(
      (renderer.axisAlignedBoundBox as BoxHelper).geometry.boundingBox as Box3,
      renderer.nodeHeight,
      renderer.nodeWidth,
    );
    const referenceDiagonal = zoom1Viewport.diagonal;

    const finalViewport = ViewPort.getForBoundingBox(
      (body.boundBox as BoxHelper).geometry.boundingBox as Box3,
      renderer.nodeHeight,
      renderer.nodeWidth,
    );

    const viewportTween = makeViewportTween(
      renderer,
      renderer.state.orthoView,
      finalViewport,
      TWEEN_TIMES.ZOOM_TO_FIT,
    );

    if (viewportTween) tweens.push(viewportTween);

    const finalDiagonal = finalViewport.diagonal;
    await renderer.state.onCameraZoomChange(referenceDiagonal / finalDiagonal);
  }

  // for normal adjustment make camera look antiparallel to normal, and align the
  // resolved camera up to be aligned to closest principal axis (+/-x,y,z) without causing gimbal lock
  // better user experience to align to an axis
  const oldLookVector = new Vector3()
    .subVectors(finalTarget, renderer.state.cameraPosition)
    .normalize();
  const newLookVector = normal.clone().negate();
  const rotAxis = new Vector3().crossVectors(newLookVector, oldLookVector);
  rotAxis.normalize();
  const angle = oldLookVector.angleTo(newLookVector);

  // establish a perfectly orthogonal up direction without affecting camera orientation
  // i.e. create a basis so rotations are reliable
  const initialUp = renderer.state.cameraUp.clone();
  const targetBasisVector = oldLookVector.clone().negate();
  const crossBasisVector = new Vector3().crossVectors(
    targetBasisVector,
    initialUp,
  );
  crossBasisVector.normalize();
  const upBasisVector = new Vector3().crossVectors(
    crossBasisVector,
    targetBasisVector,
  );

  // target basis vector is fixed based on normal to condition, so now lets transform our
  // basis by making our upBasis vector align with the nearest principle axis
  const originalBasis = new Matrix4().makeBasis(
    targetBasisVector,
    crossBasisVector,
    upBasisVector,
  );
  const transformMatrix = new Matrix4().makeRotationAxis(rotAxis, -angle);

  const transformedBasis = originalBasis.clone().premultiply(transformMatrix);

  const targetBasisTransformed = new Vector3();
  const crossBasisTransformed = new Vector3();
  const upBasisTransformed = new Vector3();
  transformedBasis.extractBasis(
    targetBasisTransformed,
    crossBasisTransformed,
    upBasisTransformed,
  );

  // find the closest principle axis projection onto the target vector plane by angle to the transformed up
  // then transform our up by that angle to align to the axis projection
  const nearestAxis = getNearestProjectedAxis(
    targetBasisTransformed,
    upBasisTransformed,
  );
  const projected = nearestAxis.projectOnPlane(targetBasisTransformed);
  projected.normalize();
  const upRotAxis = new Vector3().crossVectors(upBasisTransformed, projected);
  upRotAxis.normalize();
  const rotAngle = projected.angleTo(upBasisTransformed);
  const finalUp = upBasisTransformed
    .clone()
    .applyAxisAngle(upRotAxis, rotAngle);

  const desiredCamPosition = new Vector3().addVectors(finalTarget, normal);
  const { cameraPosition: finalPosition, maxDistance } =
    getSafePositionToPreventClippingForOrthographicCamera({
      desiredCameraPosition: desiredCamPosition,
      controlTarget: finalTarget,
      scene: renderer.scene,
    });
  ensureCameraFarSettingCompatibleWithMaxDistance(maxDistance, renderer.camera);
  const positionTween = makeCameraPositionTween(
    renderer,
    finalPosition,
    TWEEN_TIMES.NORMAL_TO,
  );

  const upTween = makeUpTween(renderer, finalUp, TWEEN_TIMES.NORMAL_TO);
  if (positionTween) tweens.push(positionTween);
  if (upTween) tweens.push(upTween);

  await Promise.all(tweens.map((tween) => tween.start()));
};

// TODO: stop tweens if snap to tweens are taking place
export const handleUpdateForceView = (
  renderer: Renderer,
  nextState: RendererState,
): [boolean, Promise<void>] => {
  if (renderer.state.forceView !== nextState.forceView && nextState.forceView) {
    switch (nextState.forceView.type) {
      case FORCE_VIEW_TYPES.NORMAL_TO:
        return [true, viewNormalTo(renderer, nextState)];
      case FORCE_VIEW_TYPES.RESET_VIEW:
        return [true, resetView(renderer, nextState)];
      case FORCE_VIEW_TYPES.ZOOM_TO_FIT:
        return [true, zoomToFit(renderer, nextState)];
      case FORCE_VIEW_TYPES.ZOOM_TO_SELECTION:
        return [true, zoomToSelection(renderer)];
    }
  }
  return [false, Promise.resolve()];
};
